/* ============================================================
   Cart drawer analytics — SlideCart (#slidecarthq).

   Reuses the size finder's exact visitor/session id scheme
   (localStorage['shsf_vid'], sessionStorage['shsf_sid']) so cart
   events, finder events, and Shopify orders can all be joined to
   one visitor. Does NOT mint new ids.

   Fires to two places, same pattern as sh-size-finder.js:
     1) GA4, via gtag/dataLayer if present (denominator source only —
        do not trust its own purchase counts for conversion truth).
     2) A dedicated Apps Script beacon -> the "Cart Drawer Tracking"
        Google Sheet (kept separate from the finder's own sheet).

   No PII in the beacon query string — only ids, counts, and flags.

   Also stamps _Visitor / _Session onto the Shopify cart via
   /cart/update.js, independent of whether the size finder was ever
   used. Without this, only finder users would carry a _Visitor
   attribute onto their order and the "did the drawer convert" join
   would silently exclude everyone who only touched the cart.

   Event hooks: SlideCart exposes window.SLIDECART_OPEN / _CLOSE /
   _UPDATE etc as globals, but they hold `undefined` at runtime —
   not safe to build on. Uses delegated document listeners on the
   same stable class names the existing accordion script already
   relies on successfully, plus a MutationObserver for open/close
   state (same technique as the footer-height tracking in
   slidecart-upsell-accordion.js).

   KNOWN GAP: cart_progress_reached (free-shipping threshold) is not
   implemented yet — no free-shipping progress bar selector could be
   confirmed in the current CSS. Confirm the feature exists and its
   markup before adding this event; shipping a guessed selector would
   silently never fire rather than fail loudly.

   NOTE on the Sheet's schema (Aug 2026): the receiving Apps Script
   writes a fixed set of columns (timestamp, event, visitor, page,
   viewport, device, timezone, session, language, referrer) — it does
   NOT dynamically add columns for arbitrary extra params. That's why
   the express checkout tracking below uses a distinct `event` value
   per payment method (cart_shop_pay_click, cart_paypal_click, etc.)
   rather than one event with a `method` param — the latter would log
   a row but silently drop which button was clicked, since there's no
   column for it and no access to the bound Apps Script from here to
   add one. If a `method`/`extra` column is ever added on the sheet
   side, this can be simplified back to a single parameterised event.
   ============================================================ */
(function () {
  if (window.__cartTrackingLoaded) return; window.__cartTrackingLoaded = true;

  var CART_ENDPOINT = 'https://script.google.com/macros/s/AKfycby_FXrL0rxOBIrtmBPPKe95O4VvvKOrAv4a3iu8x12U3Yx6gSAZuTB39mlcXScLZbqbIA/exec';

  // ---- identity + environment: same helpers as sh-size-finder.js ----
  function shsfId(store, key) {
    try {
      var v = store.getItem(key);
      if (!v) { v = Date.now().toString(36) + Math.random().toString(36).slice(2, 10); store.setItem(key, v); }
      return v;
    } catch (e) { return ''; }
  }
  function browserEnv() {
    var dev = ''; try { dev = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'mobile' : 'desktop'; } catch (e) {}
    var tz = ''; try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) {}
    return {
      page: location.pathname, referrer: document.referrer || '', device: dev,
      viewport: (window.innerWidth || '') + 'x' + (window.innerHeight || ''),
      language: navigator.language || '', timezone: tz
    };
  }
  function track(evt, info) {
    info = info || {};
    var visitor = '', session = '', k;
    try { visitor = shsfId(window.localStorage, 'shsf_vid'); } catch (e) {}
    try { session = shsfId(window.sessionStorage, 'shsf_sid'); } catch (e) {}
    var payload = { event: evt };
    for (k in info) { if (info[k] !== undefined && info[k] !== null) payload[k] = info[k]; }
    payload.visitor = visitor; payload.session = session;
    var env = browserEnv(); for (k in env) { payload[k] = env[k]; }
    try {
      if (window.gtag) { window.gtag('event', evt, payload); }
      else if (window.dataLayer && window.dataLayer.push) { window.dataLayer.push(payload); }
    } catch (e) {}
    try {
      var qs = [], pk; for (pk in payload) { qs.push(encodeURIComponent(pk) + '=' + encodeURIComponent(payload[pk])); }
      var url = CART_ENDPOINT + '?' + qs.join('&');
      try { var img = new Image(); img.src = url; }
      catch (e2) { fetch(url, { mode: 'no-cors', keepalive: true }); }
    } catch (e) {}
  }

  // ---- stamp _Visitor / _Session on the cart, independent of the finder ----
  var stampedThisSession = false;
  function stampCartAttributes() {
    if (stampedThisSession) return;
    var v = ''; try { v = shsfId(window.localStorage, 'shsf_vid'); } catch (e) {}
    var s = ''; try { s = shsfId(window.sessionStorage, 'shsf_sid'); } catch (e) {}
    if (!v) return;
    stampedThisSession = true;
    try {
      fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { '_Visitor': v, '_Session': s } })
      }).catch(function () {});
    } catch (e) {}
  }

  // ---- cart_open: drawer becomes visible ----
  var wasOpen = false;
  function checkOpenState(root) {
    var isOpen = getComputedStyle(root).display !== 'none' && root.getBoundingClientRect().width > 0;
    if (isOpen && !wasOpen) {
      track('cart_open', {});
      stampCartAttributes();
    }
    wasOpen = isOpen;
  }
  function tryInit() {
    var root = document.getElementById('slidecarthq');
    if (!root) { setTimeout(tryInit, 300); return; }
    checkOpenState(root);
    new MutationObserver(function () { checkOpenState(root); })
      .observe(root, { attributes: true, attributeFilter: ['style', 'class'], childList: true, subtree: false });
  }
  tryInit();

  // ---- cart_add: a /cart/add.js request completes ----
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isAdd = /\/cart\/add\.js(\?|$)/.test(url);
      var p = origFetch.apply(this, arguments);
      if (isAdd) {
        p.then(function (res) {
          if (res && res.ok) { track('cart_add', {}); stampCartAttributes(); }
        }).catch(function () {});
      }
      return p;
    };
  }

  // ---- cart_upsell_view: accordion opened (mirrors the existing toggle handler) ----
  document.addEventListener('click', function (e) {
    var header = e.target.closest('#slidecarthq .upsells-header');
    if (!header) return;
    if (e.target.closest('.upsells-slider-arrows')) return;
    var container = header.closest('.upsells-slider-container');
    if (!container) return;
    setTimeout(function () {
      if (container.classList.contains('gt-open')) track('cart_upsell_view', {});
    }, 0);
  });

  // ---- cart_upsell_add: add-to-cart click inside the upsell slider ----
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#slidecarthq .upsells-slider .upsell-add button');
    if (!btn) return;
    track('cart_upsell_add', {});
  }, true);

  // ---- cart_checkout_click ----
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#slidecarthq button[name="checkout"]');
    if (!btn) return;
    track('cart_checkout_click', {});
  }, true);

  // ---- express checkout button clicks (Shop Pay / PayPal / Google Pay / Apple Pay) ----
  // These render inside <shopify-accelerated-checkout-cart>'s closed shadow
  // root via named <slot>s, as light-DOM children (shop-pay-wallet-button,
  // shopify-google-pay-button, shopify-paypal-button, and Apple Pay's own
  // tag on iOS Safari — untested here since Apple Pay doesn't render
  // outside Safari, hence the regex fallback below instead of a hardcoded
  // tag name for it).
  //
  // PayPal specifically renders via a genuine cross-origin iframe inside
  // its custom element (confirmed directly: 2 <iframe>s in its light DOM,
  // vs Shop Pay/Google Pay which render via their own nested shadow root
  // instead). That raised the question of whether a PayPal click would
  // even reach a listener out here — tested directly (Aug 2026, capture-
  // phase document listener + stopPropagation to avoid actually
  // navigating away): it does. e.target is the <iframe> itself, but the
  // 'click' event still bubbles through its light-DOM ancestors up to
  // document, retargeting normally at each shadow boundary along the way
  // (composedPath included SHOPIFY-PAYPAL-BUTTON). Cross-origin
  // restrictions block reading WHAT happened inside the iframe's own
  // content, not this kind of ordinary outer-document click bubbling —
  // so one listener genuinely covers all four button types the same way,
  // no per-provider special-casing needed.
  var EXPRESS_EVENT_NAMES = {
    'shop-pay-wallet-button': 'cart_shop_pay_click',
    'shopify-paypal-button': 'cart_paypal_click',
    'shopify-google-pay-button': 'cart_google_pay_click'
  };
  function expressEventName(tag) {
    var t = String(tag || '').toLowerCase();
    if (EXPRESS_EVENT_NAMES[t]) return EXPRESS_EVENT_NAMES[t];
    // Fallback for anything not in the map above (chiefly Apple Pay, whose
    // exact tag name isn't confirmed) — derives a name instead of silently
    // doing nothing, so a new/renamed button still shows up in the sheet
    // under some readable event name rather than not being tracked at all.
    var m = t.match(/^(?:shopify-)?(.*?)-button$/);
    var label = m ? m[1].replace(/-/g, '_') : t.replace(/-/g, '_');
    return 'cart_' + label + '_click';
  }
  document.addEventListener('click', function (e) {
    var host = e.target.closest('#slidecarthq .additional-checkout-buttons shopify-accelerated-checkout-cart > *');
    if (!host) return;
    track(expressEventName(host.tagName), {});
  }, true);
})();
